import { useEffect, useState } from 'react';

import type { TicketDetail, TicketDetailAttemptMessage, TicketDetailComment } from '@nightshift/ui-types';

import { MarkdownComposer } from '../../components/markdown-composer.tsx';
import { Markdown } from '../../components/markdown.tsx';
import { Empty, Spinner } from '../../components/primitives.tsx';
import { api } from '../../live/api.ts';
import { useStore } from '../../live/context.tsx';
import { useTimeFormat } from '../../lib/display.ts';
import { clockStamp, observedTime } from '../../lib/format.ts';

const NEEDS_ANSWER = '[needs-answer]';

/**
 * Local Nightshift ticket_comments, oldest first. GitLab merge-request discussion
 * is not mirrored here — comments posted in GitLab stay on the MR.
 *
 * Composer: a comment for the next attempt, a message to the attempt running now, an optional
 * needs-answer marker, or answer-clarification when Waiting. The first two are separate on
 * purpose. A comment is prompt material and is read when an attempt starts, so anything
 * written mid-run reaches nobody until the next one — which is what 38 comments on
 * 2026-09-09 did. The composer now says which of the two you are doing before you send it.
 */
export function CommentsTab({
  detail,
  now,
  canAnswer,
  answerRequest = 0,
  onReloaded,
}: {
  detail: TicketDetail;
  now: number;
  canAnswer: boolean;
  /** Changes each time the waiting banner's Answer is pressed. */
  answerRequest?: number;
  onReloaded: () => Promise<void>;
}) {
  const store = useStore();
  const [body, setBody] = useState('');
  const [needsAnswer, setNeedsAnswer] = useState(false);
  const [steer, setSteer] = useState(false);
  const [busy, setBusy] = useState(false);

  const running = detail.attempts.find(
    (attempt) => attempt.status === 'Running' || attempt.status === 'Starting',
  );
  const canSteer = running !== undefined;
  const questionId = [...detail.comments].reverse().find((row) => row.body.includes(NEEDS_ANSWER))?.id;

  // Answer lands on the question it answers, with the cursor in the reply box.
  useEffect(() => {
    if (answerRequest === 0) return;
    const target = questionId === undefined ? null : document.getElementById(`drawer-comment-${questionId}`);
    target?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    document.getElementById('drawer-comment-body')?.focus({ preventScroll: true });
  }, [answerRequest, questionId]);
  // Turn the option off by itself when the attempt it was aimed at finishes, so the button
  // cannot send a steer against nothing and get a refusal the operator did not expect.
  const steering = steer && canSteer;

  async function post(mode: 'comment' | 'answer' | 'steer') {
    const text = body.trim();
    if (mode !== 'answer' && text.length === 0) return;
    setBusy(true);
    try {
      if (mode === 'answer') {
        await api.answer(detail.ticket.ticketKey, text || undefined, detail.ticket.version);
        store.toast('good', 'Clarification recorded');
      } else if (mode === 'steer') {
        await api.steer(detail.ticket.ticketKey, text, detail.ticket.version);
        store.toast('good', 'Sent to the running attempt');
      } else {
        const payload = needsAnswer && !text.includes(NEEDS_ANSWER) ? `${text}\n${NEEDS_ANSWER}` : text;
        await api.comment(detail.ticket.ticketKey, payload, detail.ticket.version);
        store.toast('good', 'Comment posted');
      }
      setBody('');
      setNeedsAnswer(false);
      await onReloaded();
    } catch (error) {
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="drawer-comments">
      {running?.phase ? (
        <p className="drawer-steer-heading" role="status">
          Attempt {running.ordinal}: {running.phase}
          {running.machine ? ` on ${running.machine}` : ''}
          {running.phaseAt ? ` · last progress ${new Date(running.phaseAt).toLocaleTimeString()}` : ''}
        </p>
      ) : null}
      {detail.comments.length === 0 ? (
        <Empty
          title="No comments yet."
          body="Nightshift comments live here. The GitLab merge-request thread is not mirrored onto this ticket."
        />
      ) : (
        <ol className="drawer-comment-list">
          {detail.comments.map((row) => (
            <CommentRow key={row.id} row={row} now={now} open={canAnswer && row.id === questionId} />
          ))}
        </ol>
      )}

      {detail.attemptMessages.length > 0 ? (
        <section className="drawer-steer-log">
          <h4 className="drawer-steer-heading">Sent to running attempts</h4>
          <ol className="drawer-steer-list">
            {detail.attemptMessages.map((message) => (
              <SteerRow key={message.id} message={message} now={now} />
            ))}
          </ol>
        </section>
      ) : null}

      <form
        className="drawer-comment-composer"
        onSubmit={(event) => {
          event.preventDefault();
          void post(steering ? 'steer' : 'comment');
        }}
      >
        <label className="drawer-comment-label" htmlFor="drawer-comment-body">
          {steering ? 'Message to the running attempt' : 'Comment'}
        </label>
        <MarkdownComposer
          id="drawer-comment-body"
          value={body}
          rows={5}
          placeholder={
            steering
              ? 'Tell the agent what changed. Its current run stops and starts again in the same worktree with this at the top of the prompt.'
              : canAnswer
                ? 'Answer the clarification, or leave a comment.'
                : 'Leave a comment.'
          }
          disabled={busy}
          project={detail.ticket.projectSlug}
          enableMentions
          onChange={setBody}
        />
        <p className="drawer-comment-note dim">
          {steering
            ? `Goes to attempt ${running?.ordinal ?? ''} on ${running?.machine ?? 'its machine'}. It is recorded as a comment as well.`
            : 'Read by the next attempt on this ticket. An attempt already running will not see it.'}
        </p>
        <div className="drawer-comment-tools">
          <div className="drawer-comment-flags">
            <label className="drawer-comment-flag">
              <input
                type="checkbox"
                checked={steering}
                disabled={!canSteer || busy}
                onChange={(event) => setSteer(event.target.checked)}
              />
              {canSteer ? 'Steer running attempt' : 'Steer running attempt (nothing is running)'}
            </label>
            <label className="drawer-comment-flag">
              <input
                type="checkbox"
                checked={needsAnswer}
                disabled={steering || busy}
                onChange={(event) => setNeedsAnswer(event.target.checked)}
              />
              Needs an answer
            </label>
          </div>
          <div className="drawer-comment-buttons">
            {canAnswer ? (
              <button
                type="button"
                className="btn btn-accent"
                disabled={busy}
                onClick={() => void post('answer')}
              >
                {busy ? <Spinner /> : null} Answer
              </button>
            ) : null}
            <button
              type="submit"
              className={steering ? 'btn btn-accent' : 'btn'}
              disabled={busy || body.trim().length === 0}
            >
              {busy ? <Spinner /> : null} {steering ? 'Send to the agent' : 'Post comment'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

const STEER_STATUS_LABEL: Record<TicketDetailAttemptMessage['status'], string> = {
  queued: 'Waiting for the runner',
  delivered: 'The runner has it',
  applied: 'Put in front of the agent',
  expired: 'Never reached the agent',
};

function SteerRow({ message, now }: { message: TicketDetailAttemptMessage; now: number }) {
  const format = useTimeFormat();
  // The moment that matters for each status, so the row says when it got where it got to.
  const stamp = message.appliedAt ?? message.expiredAt ?? message.deliveredAt ?? message.createdAt;
  return (
    <li className={`drawer-steer drawer-steer-${message.status}`}>
      <div className="drawer-steer-meta">
        <strong>{message.author}</strong>
        <span className="dim">
          attempt {message.attemptOrdinal ?? message.attemptId}
        </span>
        <span className={`drawer-steer-status drawer-steer-status-${message.status}`}>
          {STEER_STATUS_LABEL[message.status]}
        </span>
        <time className="dim num" dateTime={new Date(stamp).toISOString()} title={clockStamp(stamp)}>
          {observedTime(stamp, now, format)}
        </time>
      </div>
      <div className="drawer-steer-body">{message.body}</div>
      {message.status === 'expired' && message.expiredReason ? (
        <p className="drawer-steer-reason dim">{message.expiredReason}</p>
      ) : null}
    </li>
  );
}

/**
 * One comment. The `[needs-answer]` marker is what parks the ticket, not something a person
 * reads, so it is taken out of the text and the comment is labelled as a question instead.
 */
function CommentRow({ row, now, open }: { row: TicketDetailComment; now: number; open: boolean }) {
  const format = useTimeFormat();
  const asks = row.body.includes(NEEDS_ANSWER);
  const body = asks ? row.body.replaceAll(NEEDS_ANSWER, '').trim() : row.body;
  return (
    <li id={`drawer-comment-${row.id}`} className={open ? 'drawer-comment drawer-comment-question' : 'drawer-comment'}>
      <div className="drawer-comment-meta">
        <strong>{row.author}</strong>
        {asks ? <span className="drawer-comment-tag">{open ? 'Question · needs your answer' : 'Question'}</span> : null}
        <time className="dim num" dateTime={new Date(row.createdAt).toISOString()} title={clockStamp(row.createdAt)}>
          {observedTime(row.createdAt, now, format)}
        </time>
      </div>
      <div className="drawer-comment-body">
        <Markdown source={body} />
      </div>
    </li>
  );
}
